import { useState, useRef, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type ClientDto, type BookingStatus, type PaymentStatus, type BookingDto, type TrainerRole, type DeliveryMethod } from '@/api/admin'
import { DeliveryMethodFields } from '@/components/shared/DeliveryMethodFields'
import { InvoiceBookingPicker } from '@/features/admin/invoices/InvoiceBookingPicker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Breadcrumb } from '@/components/shared/Breadcrumb'
import { useTopBar } from '@/contexts/TopBarContext'
import { Link } from 'react-router-dom'
import { ResendInviteButton } from '@/components/shared/ResendInviteButton'
import ClientPortalSettingsCard from '@/features/admin/portal/ClientPortalSettingsCard'
import { AdminStaffAvatar } from '@/features/photos/PhotoAvatars'
import { Pencil, X, Plus, Upload, ChevronRight, Trash2, FileText, Check, GraduationCap, Eye, ClipboardList, AlertTriangle, UserCheck, PowerOff, MapPin, Award, Ban } from 'lucide-react'
import { VenueSelector } from '@/features/admin/venues/VenueSelector'
import { VenueFormFields } from '@/features/admin/venues/VenuesPage'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'
import { NoEligibleTrainers } from '@/components/shared/NoEligibleTrainers'
import { ActionsMenu } from '@/components/shared/ActionsMenu'
import { CancelBookingModal } from '@/components/shared/CancelBookingModal'
import { formatMoney } from '@/lib/money'
import { useAdminDates } from '@/hooks/useDates'
import { formatAddress, addressLabels } from '@/lib/address'
import { isUs } from '@/lib/dates'
import { formatTrainerConflict } from '@/lib/trainerConflicts'
import { bookingDaysFor, describeBookingDays } from '@/lib/duration'
import { useIssueProgress, IssueProgressBar } from '@/features/admin/bookings/IssueProgress'

// ─── Types ───────────────────────────────────────────────────────────────────

export type ClientFormData = Omit<ClientDto, 'id' | 'createdAt' | 'isActive'>

export const emptyClientForm: ClientFormData = {
  name: '', contactName: null, contactEmail: '', phone: null,
  addressLine1: null, addressLine2: null, city: null, county: null,
  postcode: null, country: null, notes: null,
}

const toForm = (c: ClientDto): ClientFormData => ({
  name: c.name, contactName: c.contactName, contactEmail: c.contactEmail,
  phone: c.phone, addressLine1: c.addressLine1, addressLine2: c.addressLine2,
  city: c.city, county: c.county, postcode: c.postcode, country: c.country, notes: c.notes,
})

// ─── Shared helpers ───────────────────────────────────────────────────────────

function Field({ id, label, children }: { id?: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 mt-0.5">{value || '—'}</dd>
    </div>
  )
}

// The only real distinction here is whether this person gets invited to log in to the
// portal — being a "ClientManager" grants no extra tier of admin, it just means "has an account".
const roleLabel = (r: string) => r === 'ClientManager' ? 'Portal access' : 'No login'
const hasPortalAccess = (r: string) => r === 'ClientManager'
const roleFromAccess = (checked: boolean) => checked ? 'ClientManager' : 'StaffMember'

const BOOKING_STATUSES: BookingStatus[] = ['Booked', 'Completed', 'Cancelled', 'Rejected']
const PAYMENT_STATUSES: PaymentStatus[] = ['Unpaid', 'PartiallyPaid', 'Paid', 'Void']

const statusColour: Record<BookingStatus, string> = {
  Booked:    'bg-indigo-50 text-indigo-700',
  Completed: 'bg-green-50 text-green-700',
  Cancelled: 'bg-gray-100 text-gray-500',
  Rejected:  'bg-amber-50 text-amber-700',
}
const paymentColour: Record<PaymentStatus, string> = {
  Unpaid:        'bg-red-50 text-red-700',
  PartiallyPaid: 'bg-yellow-50 text-yellow-700',
  Paid:          'bg-green-50 text-green-700',
  Void:          'bg-gray-100 text-gray-500',
}
const paymentLabel: Record<PaymentStatus, string> = {
  Unpaid: 'Unpaid', PartiallyPaid: 'Part paid', Paid: 'Paid', Void: 'Void',
}

function StatusPill({ label, colour }: { label: string; colour: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colour}`}>
      {label}
    </span>
  )
}

// ─── Client edit form ─────────────────────────────────────────────────────────

export function ClientEditForm({ initial, onSubmit, onCancel, pending, submitLabel }: {
  initial: ClientFormData
  onSubmit: (data: ClientFormData) => void
  onCancel: () => void
  pending: boolean
  submitLabel?: string
}) {
  const { region } = useAdminDates()
  const labels = addressLabels(region)
  const [form, setForm] = useState<ClientFormData>(initial)
  const set = (key: keyof ClientFormData, value: string) =>
    setForm(f => ({ ...f, [key]: value === '' ? null : value }))

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form) }} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="name" label="Organisation name *">
          <Input id="name" value={form.name} onChange={e => set('name', e.target.value)} required />
        </Field>
        <Field id="contactName" label="Contact name">
          <Input id="contactName" value={form.contactName ?? ''} onChange={e => set('contactName', e.target.value)} placeholder="e.g. Jane Smith" />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="contactEmail" label="Contact email *">
          <Input id="contactEmail" type="email" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} required />
        </Field>
        <Field id="phone" label="Phone">
          <Input id="phone" type="tel" value={form.phone ?? ''} onChange={e => set('phone', e.target.value)} placeholder="e.g. 01234 567890" />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="addressLine1" label="Address line 1">
          <Input id="addressLine1" value={form.addressLine1 ?? ''} onChange={e => set('addressLine1', e.target.value)} />
        </Field>
        <Field id="addressLine2" label="Address line 2">
          <Input id="addressLine2" value={form.addressLine2 ?? ''} onChange={e => set('addressLine2', e.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Field id="city" label="City">
          <Input id="city" value={form.city ?? ''} onChange={e => set('city', e.target.value)} />
        </Field>
        <Field id="county" label={labels.county}>
          <Input id="county" value={form.county ?? ''} onChange={e => set('county', e.target.value)} />
        </Field>
        <Field id="postcode" label={labels.postcode}>
          <Input id="postcode" value={form.postcode ?? ''} onChange={e => set('postcode', e.target.value)} />
        </Field>
        <Field id="country" label="Country">
          <Input id="country" value={form.country ?? ''} onChange={e => set('country', e.target.value)} placeholder={labels.countryPlaceholder} />
        </Field>
      </div>
      <Field id="notes" label="Notes">
        <textarea id="notes" value={form.notes ?? ''} onChange={e => set('notes', e.target.value)} rows={3}
          placeholder="Internal notes about this client…"
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none" />
      </Field>
      <div className="flex gap-2 justify-end pt-1">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={pending}>{pending ? 'Saving…' : submitLabel ?? 'Save changes'}</Button>
      </div>
    </form>
  )
}

// ─── Staff edit row ───────────────────────────────────────────────────────────

function StaffEditRow({ member, onSave, onCancel, pending }: {
  member: { id: string; firstName: string; lastName: string; email: string; role: string }
  onSave: (data: { firstName: string; lastName: string; email: string; role: string }) => void
  onCancel: () => void
  pending: boolean
}) {
  const [firstName, setFirstName] = useState(member.firstName)
  const [lastName, setLastName] = useState(member.lastName)
  const [email, setEmail] = useState(member.email)
  const [role, setRole] = useState(member.role)

  return (
    <TableRow className="bg-slate-50">
      <TableCell colSpan={5} className="py-3 px-4">
        <form
          onSubmit={e => { e.preventDefault(); onSave({ firstName, lastName, email, role }) }}
          className="flex gap-3 items-end flex-wrap"
        >
          <div className="space-y-1.5 min-w-28">
            <Label htmlFor="eFirstName">First name</Label>
            <Input id="eFirstName" value={firstName} onChange={e => setFirstName(e.target.value)} required />
          </div>
          <div className="space-y-1.5 min-w-28">
            <Label htmlFor="eLastName">Last name</Label>
            <Input id="eLastName" value={lastName} onChange={e => setLastName(e.target.value)} required />
          </div>
          <div className="space-y-1.5 flex-1 min-w-48">
            <Label htmlFor="eEmail">Email</Label>
            <Input id="eEmail" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <label className="flex items-center gap-2 h-10 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hasPortalAccess(role)}
              onChange={e => setRole(roleFromAccess(e.target.checked))}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            />
            Portal access
          </label>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={pending}>{pending ? 'Saving…' : 'Save'}</Button>
            <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      </TableCell>
    </TableRow>
  )
}

// ─── Booking form ─────────────────────────────────────────────────────────────

type TrainerAssignment = { trainerId: string; role: TrainerRole }

type BookingFormData = {
  courseId: string
  bookingDate: string
  places: number
  status: BookingStatus
  notes: string
  venueId: string | null
  attendeeIds: string[]
  trainerAssignments: TrainerAssignment[]
  deliveryMethod: DeliveryMethod
  meetingUrl: string
  meetingInstructions: string
}

const emptyBooking: BookingFormData = {
  courseId: '', bookingDate: '', places: 1, status: 'Booked', notes: '', venueId: null, attendeeIds: [], trainerAssignments: [],
  deliveryMethod: 'InPerson', meetingUrl: '', meetingInstructions: '',
}

function BookingForm({ initial, clientId, bookingId, onSubmit, onCancel, pending }: {
  initial: BookingFormData
  clientId: string
  bookingId?: string   // present when editing, for conflict exclusion
  onSubmit: (data: BookingFormData) => void
  onCancel: () => void
  pending: boolean
}) {
  const [form, setForm] = useState<BookingFormData>(initial)

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })
  const { data: staff = [] } = useQuery({
    queryKey: ['staff', clientId],
    queryFn: () => adminApi.getStaff(clientId).then(r => r.data),
  })
  const { data: trainers = [] } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => adminApi.getTrainers().then(r => r.data),
  })

  // Conflict queries — only when date + trainers chosen
  const trainerIdsWithDate = form.bookingDate
    ? form.trainerAssignments.map(a => a.trainerId)
    : []

  const conflictQueries = useQueries({
    queries: trainerIdsWithDate.map(tid => ({
      queryKey: ['trainer-conflicts', tid, form.bookingDate, bookingId],
      queryFn: () => adminApi.getTrainerConflicts(tid, form.bookingDate, bookingId).then(r => r.data),
      enabled: !!form.bookingDate,
    })),
  })
  const conflictMap: Record<string, string[]> = {}
  trainerIdsWithDate.forEach((tid, i) => {
    const conflicts = conflictQueries[i].data ?? []
    if (conflicts.length > 0) {
      conflictMap[tid] = conflicts.map(formatTrainerConflict)
    }
  })

  const toggleAttendee = (id: string) =>
    setForm(f => ({
      ...f,
      attendeeIds: f.attendeeIds.includes(id)
        ? f.attendeeIds.filter(x => x !== id)
        : [...f.attendeeIds, id],
    }))

  const setTrainerRole = (trainerId: string, role: TrainerRole | null) => {
    setForm(f => {
      const without = f.trainerAssignments.filter(a => a.trainerId !== trainerId)
      if (role === 'Lead') {
        // Auto-demote any existing lead to Assistant
        const demoted = without.map(a => a.role === 'Lead' ? { ...a, role: 'Assistant' as TrainerRole } : a)
        return { ...f, trainerAssignments: [...demoted, { trainerId, role }] }
      }
      if (role === 'Assistant') {
        // If no lead exists yet, make this trainer the lead automatically
        const hasLead = without.some(a => a.role === 'Lead')
        const effectiveRole: TrainerRole = hasLead ? 'Assistant' : 'Lead'
        return { ...f, trainerAssignments: [...without, { trainerId, role: effectiveRole }] }
      }
      return { ...f, trainerAssignments: without }
    })
  }

  const getRole = (trainerId: string): TrainerRole | null =>
    form.trainerAssignments.find(a => a.trainerId === trainerId)?.role ?? null

  // Eligible trainers — qualified for the selected course (or all if no course picked)
  const eligibleTrainers = trainers.filter(t =>
    t.isActive && (!form.courseId || t.qualifiedCourseIds.includes(form.courseId))
  )

  // Staffing validation
  const leadCount = form.trainerAssignments.filter(a => a.role === 'Lead').length
  const staffingWarning = form.trainerAssignments.length > 0 && leadCount === 0
    ? 'No lead trainer assigned.'
    : null

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form) }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="bCourse" label="Course *">
          <select
            id="bCourse"
            value={form.courseId}
            onChange={e => setForm(f => ({ ...f, courseId: e.target.value, trainerAssignments: [] }))}
            required
            className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="">Select a course…</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>
                {c.code ? `${c.code} — ` : ''}{c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field id="bDate" label="Date *">
          <Input id="bDate" type="date" value={form.bookingDate}
            onChange={e => setForm(f => ({ ...f, bookingDate: e.target.value }))} required />
        </Field>
      </div>
      {!bookingId && describeBookingDays(courses.find(c => c.id === form.courseId), form.bookingDate) && (
        <p className="text-xs text-gray-500 -mt-2">{describeBookingDays(courses.find(c => c.id === form.courseId), form.bookingDate)}</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field id="bPlaces" label="Places *">
          <Input id="bPlaces" type="number" min="1" step="1" value={form.places === 0 ? '' : form.places}
            onChange={e => { const v = e.target.value; setForm(f => ({ ...f, places: v === '' ? 0 : parseInt(v) || 0 })) }}
            onBlur={() => setForm(f => ({ ...f, places: Math.max(1, f.places) }))} required />
        </Field>
        <Field id="bStatus" label="Status">
          <select
            id="bStatus"
            value={form.status}
            onChange={e => setForm(f => ({ ...f, status: e.target.value as BookingStatus }))}
            className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {BOOKING_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field id="bNotes" label="Notes">
          <Input id="bNotes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
        </Field>
      </div>

      <DeliveryMethodFields
        value={{ deliveryMethod: form.deliveryMethod, meetingUrl: form.meetingUrl, meetingInstructions: form.meetingInstructions }}
        onChange={next => setForm(f => ({ ...f, ...next }))}
        venueField={
          <VenueSelector
            id="bVenue"
            value={form.venueId}
            onChange={v => setForm(f => ({ ...f, venueId: v }))}
          />
        }
      />

      {/* Trainer assignment */}
      <div>
        <Label className="mb-2 block">Trainer assignment</Label>
        {eligibleTrainers.length === 0 ? (
          <NoEligibleTrainers
            hasCourse={!!form.courseId}
            hasAnyTrainers={trainers.some(t => t.isActive)}
          />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {eligibleTrainers.map(t => {
              const role = getRole(t.id)
              const conflict = conflictMap[t.id]
              const assigned = role !== null
              const isLead = role === 'Lead'
              return (
                <div key={t.id} className={`rounded-lg border p-2.5 transition-colors ${
                  assigned ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white'
                }`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-800 truncate">{t.firstName} {t.lastName}</span>
                    {conflict && (
                      <span title={`Double-booked: ${conflict.join('; ')}`}>
                        <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setTrainerRole(t.id, assigned ? null : 'Assistant')}
                      className={`flex-1 text-xs py-0.5 rounded border cursor-pointer transition-colors ${
                        assigned
                          ? 'bg-indigo-100 text-indigo-700 border-indigo-300 hover:bg-indigo-200'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {assigned ? 'Assigned ✓' : 'Assign'}
                    </button>
                    {assigned && (
                      <button
                        type="button"
                        onClick={() => setTrainerRole(t.id, isLead ? 'Assistant' : 'Lead')}
                        title={isLead ? 'Remove lead role' : 'Set as lead trainer'}
                        className={`text-sm px-1.5 py-0.5 rounded border cursor-pointer transition-colors ${
                          isLead
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'border-gray-300 text-gray-400 hover:border-indigo-400 hover:text-indigo-500'
                        }`}
                      >
                        ★
                      </button>
                    )}
                  </div>
                  {conflict && (
                    <p className="text-[10px] text-amber-600 mt-1 truncate" title={conflict.join(', ')}>
                      ⚠ Double-booked this day
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {staffingWarning && (
          <div className="mt-2 flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
            <AlertTriangle size={14} className="shrink-0" />
            {staffingWarning}
          </div>
        )}
      </div>

      {staff.length > 0 && (
        <div>
          <Label className="mb-2 block">Attendees (optional)</Label>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4">
            {staff.map(m => {
              const selected = form.attendeeIds.includes(m.id)
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleAttendee(m.id)}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left transition-colors ${
                    selected
                      ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {selected && <Check size={13} className="shrink-0 text-indigo-600" />}
                  <span className="truncate">{m.firstName} {m.lastName}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end pt-1">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={pending}>{pending ? 'Saving…' : 'Save booking'}</Button>
      </div>
    </form>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

type Tab = 'staff' | 'bookings' | 'invoices' | 'venues' | 'portal'

export default function ClientDetailPage() {
  const { formatDate, region } = useAdminDates()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('tab') as Tab | null) ?? 'staff'
  const setActiveTab = (tab: Tab) => setSearchParams({ tab }, { replace: true })

  // Client
  const { data: client, isLoading } = useQuery({
    queryKey: ['clients', id],
    queryFn: () => adminApi.getClient(id!).then(r => r.data),
    enabled: !!id,
  })
  const setActiveMutation = useMutation({
    mutationFn: (isActive: boolean) => adminApi.setClientActive(id!, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', id] })
      queryClient.invalidateQueries({ queryKey: ['client-summaries'] })
    },
  })

  const [previewing, setPreviewing] = useState(false)
  const handlePreview = async () => {
    setPreviewing(true)
    // Open the tab synchronously, inside the click's user-gesture context — mobile Safari
    // (and some mobile Chrome) silently blocks window.open() called after an await, since by
    // then it's no longer seen as gesture-triggered. Navigate this already-open tab once the
    // token arrives instead of opening a new one at that point.
    // NOTE: 'noopener' must NOT be passed here — it makes window.open() return null (no
    // reference to navigate later), which defeats this whole pattern.
    const win = window.open('', '_blank')
    try {
      const { data } = await adminApi.createPreviewToken(id!)
      const url = `/portal/preview#token=${encodeURIComponent(data.token)}&client=${encodeURIComponent(data.clientName)}`
      if (win) win.location.href = url
      else window.open(url, '_blank', 'noopener')
    } finally {
      setPreviewing(false)
    }
  }

  const [editing, setEditing] = useState(false)
  const updateMutation = useMutation({
    mutationFn: (data: ClientFormData) => adminApi.updateClient(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', id] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      setEditing(false)
    },
    onError: () => alert('Failed to save changes.'),
  })

  // Staff
  const csvInputRef = useRef<HTMLInputElement>(null)
  const [showAddStaff, setShowAddStaff] = useState(false)
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null)
  const [showBulkTraining, setShowBulkTraining] = useState(false)
  const [bulkCourseId, setBulkCourseId] = useState('')
  const [bulkDate, setBulkDate] = useState('')
  const [bulkExpiry, setBulkExpiry] = useState('')
  const [bulkStaffIds, setBulkStaffIds] = useState<string[]>([])
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('StaffMember')
  const [importMessage, setImportMessage] = useState('')

  const { data: staff = [], isLoading: staffLoading } = useQuery({
    queryKey: ['staff', id],
    queryFn: () => adminApi.getStaff(id!).then(r => r.data),
    enabled: !!id,
  })
  const { paged: pagedStaff, page: staffPage, setPage: setStaffPage, totalPages: staffTotalPages, total: staffTotal, pageSize: staffPageSize, start: staffStart } = usePagination(staff)
  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })
  const addStaffMutation = useMutation({
    mutationFn: () => adminApi.addStaff(id!, { email, firstName, lastName, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', id] })
      setShowAddStaff(false)
      setFirstName(''); setLastName(''); setEmail(''); setRole('StaffMember')
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(detail ?? 'Failed to add staff member.')
    },
  })

  const updateStaffMutation = useMutation({
    mutationFn: (data: { id: string; firstName: string; lastName: string; email: string; role: string }) =>
      adminApi.updateStaff(data.id, { firstName: data.firstName, lastName: data.lastName, email: data.email, role: data.role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', id] })
      setEditingStaffId(null)
    },
    onError: () => alert('Failed to update staff member.'),
  })
  const importMutation = useMutation({
    mutationFn: (file: File) => adminApi.importStaff(id!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', id] })
      setImportMessage('Import complete.')
      setTimeout(() => setImportMessage(''), 4000)
    },
    onError: () => setImportMessage('Import failed. Check your CSV format.'),
  })
  const bulkTrainingMutation = useMutation({
    mutationFn: () => adminApi.bulkAddTrainingRecords(id!, {
      courseId: bulkCourseId,
      completedDate: bulkDate,
      expiryDate: bulkExpiry || null,
      staffMemberIds: bulkStaffIds,
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['staff', id] })
      const { trainingRecordsCreated: created, alreadyExisted: skipped, certificatesGenerated: certs } = res.data
      const parts = []
      if (created > 0) parts.push(`${created} record${created === 1 ? '' : 's'} created`)
      if (skipped > 0) parts.push(`${skipped} skipped (already had this course)`)
      if (certs > 0) parts.push(`${certs} certificate${certs === 1 ? '' : 's'} generated`)
      alert(parts.length ? parts.join(', ') + '.' : 'No new records created.')
      setShowBulkTraining(false)
      setBulkCourseId(''); setBulkDate(''); setBulkExpiry(''); setBulkStaffIds([])
    },
    onError: () => alert('Failed to add training records.'),
  })

  const handleCsvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) importMutation.mutate(file)
    e.target.value = ''
  }

  // Bookings
  const [showAddBooking, setShowAddBooking] = useState(false)
  const [completingBookingId, setCompletingBookingId] = useState<string | null>(null)
  const [passingAttendeeIds, setPassingAttendeeIds] = useState<string[]>([])

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['bookings', id],
    queryFn: () => adminApi.getClientBookings(id!).then(r => r.data),
    enabled: !!id && (activeTab === 'bookings' || activeTab === 'invoices'),
  })
  const { paged: pagedBookings, page: bookingsPage, setPage: setBookingsPage, totalPages: bookingsTotalPages, total: bookingsTotal, pageSize: bookingsPageSize, start: bookingsStart } = usePagination(bookings)

  const clientInvoices = useMemo(() =>
    bookings
      .filter(b => b.invoice !== null)
      .map(b => ({ ...b.invoice!, courseName: b.courseName, courseCode: b.courseCode }))
      .sort((a, b) => b.invoiceNumber.localeCompare(a.invoiceNumber)),
    [bookings]
  )
  const { paged: pagedInvoices, page: invoicesPage, setPage: setInvoicesPage, totalPages: invoicesTotalPages, total: invoicesTotal, pageSize: invoicesPageSize, start: invoicesStart } = usePagination(clientInvoices)

  const createBookingMutation = useMutation({
    mutationFn: (data: BookingFormData) => adminApi.createBooking(id!, {
      ...data, notes: data.notes || null, venueId: data.deliveryMethod === 'Online' ? null : data.venueId,
      ...bookingDaysFor(courses.find(c => c.id === data.courseId), data.bookingDate),
      meetingUrl: data.deliveryMethod === 'Online' ? (data.meetingUrl.trim() || null) : null,
      meetingInstructions: data.deliveryMethod === 'Online' ? (data.meetingInstructions.trim() || null) : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings', id] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      setShowAddBooking(false)
    },
    onError: () => alert('Failed to create booking.'),
  })

  const deleteBookingMutation = useMutation({
    mutationFn: adminApi.deleteBooking,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bookings', id] }),
    onError: () => alert('Cannot delete a booking that has an invoice.'),
  })

  const completeBookingMutation = useMutation({
    mutationFn: async ({ bookingId, ids }: { bookingId: string; ids: string[] }) => {
      await adminApi.completeBooking(bookingId, ids)
      return adminApi.issueCertificates(bookingId)
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['bookings', id] })
      setCompletingBookingId(null)
      const { trainingRecordsCreated: created, alreadyExisted: skipped, certificatesGenerated: certs } = res.data
      const parts = []
      if (created > 0) parts.push(`${created} training record${created === 1 ? '' : 's'} created`)
      if (skipped > 0) parts.push(`${skipped} skipped (record already existed for this course)`)
      if (certs > 0) parts.push(`${certs} certificate${certs === 1 ? '' : 's'} generated`)
      if (parts.length === 0) parts.push('no new records created')
      alert(`Booking marked complete. ${parts.join(', ')}.`)
    },
    onError: () => alert('Failed to complete booking.'),
  })

  const completeProgress = useIssueProgress(completingBookingId, completeBookingMutation.isPending)

  const issueCertificatesMutation = useMutation({
    mutationFn: (bookingId: string) => adminApi.issueCertificates(bookingId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bookings', id] }),
    onError: () => alert('Failed to issue certificates.'),
  })

  const [cancelTarget, setCancelTarget] = useState<{ id: string; courseName: string; clientName: string } | null>(null)
  const cancelMutation = useMutation({
    mutationFn: ({ bookingId, notifyClient }: { bookingId: string; notifyClient: boolean }) =>
      adminApi.cancelBooking(bookingId, notifyClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings', id] })
      setCancelTarget(null)
    },
    onError: () => alert('Failed to cancel booking — it may already be completed or invoiced.'),
  })

  const openCompletion = (booking: BookingDto) => {
    setCompletingBookingId(booking.id)
    setShowAddBooking(false)
    setPassingAttendeeIds(booking.attendees.map(a => a.staffMemberId))
  }

  const createInvoiceMutation = useMutation({
    mutationFn: (bookingId: string) => adminApi.createInvoice(id!, { bookingIds: [bookingId], dueDate: null, notes: null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bookings', id] }),
    onError: () => alert('Failed to generate invoice.'),
  })

  const [showInvoicePicker, setShowInvoicePicker] = useState(false)
  const [invoicePickerError, setInvoicePickerError] = useState<string | null>(null)
  const bundleInvoiceMutation = useMutation({
    mutationFn: (bookingIds: string[]) => adminApi.createInvoice(id!, { bookingIds, dueDate: null, notes: null }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['bookings', id] })
      setShowInvoicePicker(false)
      navigate(`/admin/invoices/${res.data.id}`)
    },
    onError: () => setInvoicePickerError('Failed to create the invoice. Please try again.'),
  })

  const updateInvoiceMutation = useMutation({
    mutationFn: ({ invoiceId, status }: { invoiceId: string; status: PaymentStatus }) =>
      adminApi.updateInvoice(invoiceId, { paymentStatus: status, dueDate: null, notes: null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bookings', id] }),
  })

  useTopBar(
    <Breadcrumb items={[
      { label: 'Clients', to: '/admin/clients' },
      { label: client?.name ?? '…' },
    ]} />
  )

  if (isLoading) return <p className="text-gray-500">Loading…</p>
  if (!client) return <p className="text-gray-500">Client not found.</p>

  const addressText = formatAddress({
    line1: client.addressLine1, line2: client.addressLine2, city: client.city,
    county: client.county, postcode: client.postcode, country: client.country,
  }, region)

  return (
    <div className="space-y-6">
      {/* Deactivated banner */}
      {!client.isActive && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3">
          <PowerOff size={16} className="text-amber-500 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">This client is deactivated</p>
            <p className="text-xs text-amber-700 mt-0.5">Staff cannot log in to the client portal. Reactivate to restore access.</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 gap-1.5 text-green-700 border-green-200 hover:bg-green-50"
            onClick={() => setActiveMutation.mutate(true)}
            disabled={setActiveMutation.isPending}
          >
            Reactivate
          </Button>
        </div>
      )}

      {/* ── Details card ── */}
      <Card>
        <CardHeader className="pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-xl">{client.name}</CardTitle>
            {client.contactName && <p className="text-sm text-gray-500 mt-0.5">{client.contactName}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {client.isActive && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                onClick={handlePreview}
                disabled={previewing}
              >
                <Eye size={14} /> {previewing ? 'Opening…' : 'View client site'}
              </Button>
            )}
            {!editing ? (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditing(true)}>
                <Pencil size={14} /> Edit
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => setEditing(false)}>
                <X size={15} />
              </Button>
            )}
            {client.isActive && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  if (confirm(`Deactivate "${client.name}"? Staff will no longer be able to log in to the client portal.`))
                    setActiveMutation.mutate(false)
                }}
                disabled={setActiveMutation.isPending}
              >
                <PowerOff size={14} /> Deactivate
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {editing ? (
            <ClientEditForm
              initial={toForm(client)}
              onSubmit={data => updateMutation.mutate(data)}
              onCancel={() => setEditing(false)}
              pending={updateMutation.isPending}
            />
          ) : (
            <dl className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-8 md:grid-cols-3 lg:grid-cols-4">
              <InfoRow label="Contact email" value={client.contactEmail} />
              <InfoRow label="Phone" value={client.phone} />
              {addressText && (
                <div className="col-span-2">
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">Address</dt>
                  <dd className="text-sm font-medium text-gray-900 mt-0.5">{addressText}</dd>
                </div>
              )}
              {client.notes && (
                <div className="col-span-2 lg:col-span-4">
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">Notes</dt>
                  <dd className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{client.notes}</dd>
                </div>
              )}
            </dl>
          )}
        </CardContent>
      </Card>

      {/* ── Tabs ── */}
      <div>
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-gray-200 mb-4">
          {(['staff', 'bookings', 'invoices', 'venues', 'portal'] as Tab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px cursor-pointer ${
                activeTab === tab
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── Staff tab ── */}
        {activeTab === 'staff' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-2 ml-auto flex-wrap justify-end">
                <Button variant="outline" size="sm" className="gap-2"
                  onClick={() => csvInputRef.current?.click()} disabled={importMutation.isPending}>
                  <Upload size={15} /> {importMutation.isPending ? 'Importing…' : 'Import CSV'}
                </Button>
                <input ref={csvInputRef} type="file" accept=".csv" className="hidden" onChange={handleCsvChange} />
                <Button variant="outline" size="sm" className="gap-2"
                  onClick={() => { setShowBulkTraining(v => !v); setShowAddStaff(false) }}>
                  <ClipboardList size={15} /> Record training
                </Button>
                <Button size="sm" className="gap-2" onClick={() => setShowAddStaff(v => !v)}>
                  <Plus size={15} /> Add staff
                </Button>
              </div>
            </div>

            {importMessage && (
              <p className={`text-sm mb-3 ${importMessage.includes('failed') ? 'text-red-600' : 'text-green-700'}`}>
                {importMessage}
              </p>
            )}

            {showBulkTraining && (
              <Card className="mb-3">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Record training completion</CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400"
                    onClick={() => setShowBulkTraining(false)}>
                    <X size={15} />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="bulkCourse">Course *</Label>
                      <select
                        id="bulkCourse"
                        value={bulkCourseId}
                        onChange={e => {
                          const cid = e.target.value
                          setBulkCourseId(cid)
                          const course = courses.find(c => c.id === cid)
                          if (course?.defaultExpiryMonths && bulkDate) {
                            const d = new Date(bulkDate + 'T00:00:00')
                            d.setMonth(d.getMonth() + course.defaultExpiryMonths)
                            setBulkExpiry(d.toISOString().slice(0, 10))
                          } else if (!course?.defaultExpiryMonths) {
                            setBulkExpiry('')
                          }
                        }}
                        className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        <option value="">Select a course…</option>
                        {courses.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.code ? `${c.code} — ` : ''}{c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="bulkDate">Completed date *</Label>
                      <Input
                        id="bulkDate"
                        type="date"
                        value={bulkDate}
                        onChange={e => {
                          setBulkDate(e.target.value)
                          const course = courses.find(c => c.id === bulkCourseId)
                          if (course?.defaultExpiryMonths && e.target.value) {
                            const d = new Date(e.target.value + 'T00:00:00')
                            d.setMonth(d.getMonth() + course.defaultExpiryMonths)
                            setBulkExpiry(d.toISOString().slice(0, 10))
                          }
                        }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="bulkExpiry">Expiry date</Label>
                      <Input
                        id="bulkExpiry"
                        type="date"
                        value={bulkExpiry}
                        onChange={e => setBulkExpiry(e.target.value)}
                        placeholder="Auto-calculated"
                      />
                    </div>
                  </div>

                  {staff.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Staff members *</Label>
                        <button
                          type="button"
                          className="text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer"
                          onClick={() =>
                            setBulkStaffIds(
                              bulkStaffIds.length === staff.length ? [] : staff.map(s => s.id)
                            )
                          }
                        >
                          {bulkStaffIds.length === staff.length ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4">
                        {staff.map(m => {
                          const selected = bulkStaffIds.includes(m.id)
                          return (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => setBulkStaffIds(ids =>
                                selected ? ids.filter(x => x !== m.id) : [...ids, m.id]
                              )}
                              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                                selected
                                  ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
                              }`}
                            >
                              {selected && <Check size={13} className="shrink-0 text-indigo-600" />}
                              <span className="truncate">{m.firstName} {m.lastName}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-1">
                    <Button
                      disabled={!bulkCourseId || !bulkDate || bulkStaffIds.length === 0 || bulkTrainingMutation.isPending}
                      onClick={() => bulkTrainingMutation.mutate()}
                    >
                      <ClipboardList size={14} className="mr-1.5" />
                      {bulkTrainingMutation.isPending
                        ? 'Creating records…'
                        : `Create ${bulkStaffIds.length} record${bulkStaffIds.length === 1 ? '' : 's'}`}
                    </Button>
                    <Button variant="outline" onClick={() => setShowBulkTraining(false)}>Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {showAddStaff && (
              <Card className="mb-3">
                <CardContent className="pt-5">
                  <form onSubmit={e => { e.preventDefault(); addStaffMutation.mutate() }}
                    className="flex gap-4 items-end flex-wrap">
                    <div className="space-y-1.5 min-w-32">
                      <Label htmlFor="firstName">First name</Label>
                      <Input id="firstName" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                    </div>
                    <div className="space-y-1.5 min-w-32">
                      <Label htmlFor="lastName">Last name</Label>
                      <Input id="lastName" value={lastName} onChange={e => setLastName(e.target.value)} required />
                    </div>
                    <div className="space-y-1.5 flex-1 min-w-48">
                      <Label htmlFor="staffEmail">Email</Label>
                      <Input id="staffEmail" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                    </div>
                    <label className="flex items-center gap-2 h-10 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hasPortalAccess(role)}
                        onChange={e => setRole(roleFromAccess(e.target.checked))}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                      />
                      Portal access
                    </label>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={addStaffMutation.isPending}>
                        {addStaffMutation.isPending ? 'Saving…' : role === 'ClientManager' ? 'Add & invite' : 'Add staff member'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowAddStaff(false)}>Cancel</Button>
                    </div>
                  </form>
                  <p className="text-xs text-gray-400 mt-2">
                    Portal access invites them by email to log in and view the whole team's training records and certificates.
                  </p>
                </CardContent>
              </Card>
            )}

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Access</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffLoading && (
                    <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">Loading…</TableCell></TableRow>
                  )}
                  {!staffLoading && staff.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">No staff yet.</TableCell></TableRow>
                  )}
                  {pagedStaff.map(member => (
                    editingStaffId === member.id ? (
                      <StaffEditRow
                        key={member.id}
                        member={member}
                        onSave={(data) => updateStaffMutation.mutate({ id: member.id, ...data })}
                        onCancel={() => setEditingStaffId(null)}
                        pending={updateStaffMutation.isPending}
                      />
                    ) : (
                      <TableRow
                        key={member.id}
                        className="hover:bg-gray-50"
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2.5">
                            <AdminStaffAvatar member={member} clientId={id!} />
                            <button
                              type="button"
                              className="hover:text-indigo-600 hover:underline cursor-pointer text-left"
                              onClick={() => navigate(`/admin/clients/${id}/staff/${member.id}/training`)}
                            >
                              {member.firstName} {member.lastName}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">{member.email}</TableCell>
                        <TableCell className="text-gray-600 text-sm">
                          {roleLabel(member.role)}
                          {member.invitePending && <div><ResendInviteButton send={() => adminApi.resendStaffInvite(member.id)} /></div>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={member.isActive ? 'success' : 'neutral'}>
                            {member.isActive ? 'Active' : 'Invited'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost" size="sm"
                              className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                              onClick={() => setEditingStaffId(member.id)}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                              onClick={() => navigate(`/admin/clients/${id}/staff/${member.id}/training`)}
                            >
                              <ChevronRight size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  ))}
                </TableBody>
              </Table>
              <TablePagination page={staffPage} totalPages={staffTotalPages} total={staffTotal} pageSize={staffPageSize} start={staffStart} onPage={setStaffPage} />
            </Card>
          </div>
        )}

        {/* ── Bookings tab ── */}
        {activeTab === 'bookings' && (
          <div>
            <div className="flex justify-end gap-2 mb-3">
              <Button variant="outline" size="sm" className="gap-2" onClick={() => { setInvoicePickerError(null); setShowInvoicePicker(true) }}>
                <FileText size={15} /> Bundle into invoice
              </Button>
              <Button size="sm" className="gap-2" onClick={() => setShowAddBooking(v => !v)}>
                <Plus size={15} /> Add client booking
              </Button>
            </div>

            {showInvoicePicker && (
              <InvoiceBookingPicker
                clientId={id!}
                title="Create invoice"
                confirmLabel="Create invoice"
                pending={bundleInvoiceMutation.isPending}
                error={invoicePickerError}
                onConfirm={bookingIds => bundleInvoiceMutation.mutate(bookingIds)}
                onClose={() => setShowInvoicePicker(false)}
              />
            )}

            {showAddBooking && (
              <Card className="mb-4">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-base">New client booking</CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => setShowAddBooking(false)}>
                    <X size={15} />
                  </Button>
                </CardHeader>
                <CardContent>
                  <BookingForm
                    initial={emptyBooking}
                    clientId={id!}
                    onSubmit={data => createBookingMutation.mutate(data)}
                    onCancel={() => setShowAddBooking(false)}
                    pending={createBookingMutation.isPending}
                  />
                </CardContent>
              </Card>
            )}

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead className="text-right">Places</TableHead>
                    <TableHead>Trainers</TableHead>
                    <TableHead>Attendees</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookingsLoading && (
                    <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-8">Loading…</TableCell></TableRow>
                  )}
                  {!bookingsLoading && bookings.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-8">No bookings yet.</TableCell></TableRow>
                  )}
                  {pagedBookings.map(booking => (
                    <>
                      <TableRow key={booking.id} className={completingBookingId === booking.id ? 'bg-slate-50' : undefined}>
                        <TableCell className="text-sm text-gray-700 whitespace-nowrap">
                          {formatDate(booking.bookingDate, { day: 'numeric', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-sm">{booking.courseName}</p>
                          {booking.courseCode && <p className="text-xs text-gray-400 font-mono">{booking.courseCode}</p>}
                        </TableCell>
                        <TableCell className="text-right text-sm text-gray-700">{booking.places}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {booking.trainerAssignments.length === 0
                            ? <span className="text-gray-300 text-xs">—</span>
                            : <div className="space-y-0.5">
                                {booking.trainerAssignments.map(t => (
                                  <div key={t.trainerId} className="flex items-center gap-1 text-xs">
                                    <UserCheck size={11} className={t.role === 'Lead' ? 'text-indigo-500' : 'text-slate-400'} />
                                    <span className="text-gray-700">{t.firstName} {t.lastName}</span>
                                    <span className={`text-[10px] ${t.role === 'Lead' ? 'text-indigo-500' : 'text-gray-400'}`}>{t.role}</span>
                                  </div>
                                ))}
                              </div>
                          }
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {booking.attendees.length === 0
                            ? <span className="text-gray-400">—</span>
                            : (() => {
                                const mismatch = booking.attendees.length !== booking.places
                                return (
                                  <span
                                    title={booking.attendees.map(a => `${a.firstName} ${a.lastName}`).join(', ')}
                                    className={`inline-flex items-center gap-1 ${mismatch ? 'text-amber-600 font-medium' : ''}`}
                                  >
                                    {mismatch && <AlertTriangle size={12} />}
                                    {booking.attendees.length} / {booking.places}
                                  </span>
                                )
                              })()
                          }
                        </TableCell>
                        <TableCell>
                          <StatusPill label={booking.status} colour={statusColour[booking.status]} />
                        </TableCell>
                        <TableCell>
                          {booking.invoice ? (
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/admin/invoices/${booking.invoice.id}`}
                                className="text-xs font-mono text-indigo-600 hover:text-indigo-800 hover:underline"
                                onClick={e => e.stopPropagation()}
                                title={booking.invoice.bookingCount > 1 ? `Bundled with ${booking.invoice.bookingCount - 1} other booking${booking.invoice.bookingCount > 2 ? 's' : ''}` : undefined}
                              >
                                {booking.invoice.invoiceNumber}{booking.invoice.bookingCount > 1 && ' +'}
                              </Link>
                              <select
                                value={booking.invoice.paymentStatus}
                                onChange={e => updateInvoiceMutation.mutate({
                                  invoiceId: booking.invoice!.id,
                                  status: e.target.value as PaymentStatus,
                                })}
                                className={`text-xs rounded-full px-2 py-0.5 border-0 font-medium cursor-pointer ${paymentColour[booking.invoice.paymentStatus]}`}
                              >
                                {PAYMENT_STATUSES.map(s => (
                                  <option key={s} value={s}>{paymentLabel[s]}</option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1 text-xs text-gray-500 hover:text-gray-800 px-2"
                              onClick={() => createInvoiceMutation.mutate(booking.id)}
                              disabled={createInvoiceMutation.isPending}
                            >
                              <FileText size={13} /> Generate
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <ActionsMenu
                            items={[
                              ...(booking.attendees.length > 0 && booking.status !== 'Completed' && booking.status !== 'Cancelled' ? [{
                                label: completingBookingId === booking.id ? 'Cancel completing' : 'Complete booking',
                                icon: <GraduationCap size={14} className="text-green-600" />,
                                onClick: () => completingBookingId === booking.id
                                  ? setCompletingBookingId(null)
                                  : openCompletion(booking),
                              }] : []),
                              ...(booking.status === 'Completed' && !booking.certificatesIssuedAt ? [{
                                label: 'Issue certificates',
                                icon: <Award size={14} className="text-indigo-600" />,
                                onClick: () => issueCertificatesMutation.mutate(booking.id),
                                disabled: issueCertificatesMutation.isPending,
                              }] : []),
                              {
                                label: 'Edit booking',
                                icon: <Pencil size={14} className="text-gray-500" />,
                                onClick: () => navigate(`/admin/bookings/${booking.id}`),
                              },
                              ...(booking.status === 'Booked' && !booking.invoice ? [{
                                label: 'Cancel booking',
                                icon: <Ban size={14} className="text-amber-600" />,
                                onClick: () => setCancelTarget({ id: booking.id, courseName: booking.courseName, clientName: client?.name ?? '' }),
                              }] : []),
                              ...(booking.status === 'Cancelled' ? [{
                                label: 'Delete booking',
                                icon: <Trash2 size={14} className="text-red-600" />,
                                onClick: () => {
                                  if (confirm(`Delete this booking for "${booking.courseName}"?`))
                                    deleteBookingMutation.mutate(booking.id)
                                },
                                disabled: deleteBookingMutation.isPending,
                                danger: true,
                              }] : []),
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                      {completingBookingId === booking.id && (
                        <TableRow key={`${booking.id}-complete`}>
                          <TableCell colSpan={8} className="py-4 px-4 bg-slate-50">
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm font-medium text-gray-900">Complete booking — select who passed</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  A training record will be created for each ticked staff member.
                                  Untick anyone who did not pass or attend.
                                </p>
                              </div>
                              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4">
                                {booking.attendees.map(a => {
                                  const selected = passingAttendeeIds.includes(a.staffMemberId)
                                  return (
                                    <button
                                      key={a.staffMemberId}
                                      type="button"
                                      onClick={() => setPassingAttendeeIds(ids =>
                                        selected
                                          ? ids.filter(x => x !== a.staffMemberId)
                                          : [...ids, a.staffMemberId]
                                      )}
                                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left transition-colors ${
                                        selected
                                          ? 'border-green-400 bg-green-50 text-green-800'
                                          : 'border-gray-200 bg-white text-gray-400 line-through'
                                      }`}
                                    >
                                      {selected && <Check size={13} className="shrink-0 text-green-600" />}
                                      <span className="truncate">{a.firstName} {a.lastName}</span>
                                    </button>
                                  )
                                })}
                              </div>
                              {completeBookingMutation.isPending && <IssueProgressBar progress={completeProgress} />}
                              <div className="flex gap-2 pt-1">
                                <Button
                                  size="sm"
                                  className="gap-1.5"
                                  disabled={completeBookingMutation.isPending}
                                  onClick={() => completeBookingMutation.mutate({
                                    bookingId: booking.id,
                                    ids: passingAttendeeIds,
                                  })}
                                >
                                  <GraduationCap size={14} />
                                  {completeBookingMutation.isPending
                                    ? 'Creating records…'
                                    : `Complete & create ${passingAttendeeIds.length} record${passingAttendeeIds.length === 1 ? '' : 's'}`
                                  }
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setCompletingBookingId(null)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
              <TablePagination page={bookingsPage} totalPages={bookingsTotalPages} total={bookingsTotal} pageSize={bookingsPageSize} start={bookingsStart} onPage={setBookingsPage} />
            </Card>
            {cancelTarget && (
              <CancelBookingModal
                booking={cancelTarget}
                onClose={() => setCancelTarget(null)}
                onConfirm={notifyClient => cancelMutation.mutate({ bookingId: cancelTarget.id, notifyClient })}
                isPending={cancelMutation.isPending}
              />
            )}
          </div>
        )}

        {/* ── Invoices tab ── */}
        {activeTab === 'invoices' && (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookingsLoading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">Loading…</TableCell>
                  </TableRow>
                )}
                {!bookingsLoading && clientInvoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No invoices yet. Generate one from the Bookings tab.
                    </TableCell>
                  </TableRow>
                )}
                {pagedInvoices.map(inv => (
                  <TableRow key={inv.id} className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/invoices/${inv.id}`)}>
                    <TableCell className="font-mono text-sm text-indigo-600">{inv.invoiceNumber}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{inv.courseName}</p>
                      {inv.courseCode && <p className="text-xs text-gray-400 font-mono">{inv.courseCode}</p>}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(inv.issuedDate, { day: 'numeric', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {inv.dueDate
                        ? formatDate(inv.dueDate, { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium text-gray-900">
                      {formatMoney(inv.amount, inv.currency)}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentColour[inv.paymentStatus]}`}>
                        {paymentLabel[inv.paymentStatus]}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination page={invoicesPage} totalPages={invoicesTotalPages} total={invoicesTotal} pageSize={invoicesPageSize} start={invoicesStart} onPage={setInvoicesPage} />
          </Card>
        )}

        {/* ── Venues tab ── */}
        {activeTab === 'portal' && id && <ClientPortalSettingsCard clientId={id} />}

        {activeTab === 'venues' && id && (
          <ClientVenuesTab clientId={id} />
        )}
      </div>
    </div>
  )
}

function ClientVenuesTab({ clientId }: { clientId: string }) {
  const { region } = useAdminDates()
  const venueSummary = (v: { addressLine1?: string | null; city?: string | null; county?: string | null; postcode?: string | null }) =>
    formatAddress({ line1: v.addressLine1, city: v.city, county: isUs(region) ? v.county : null, postcode: v.postcode }, region)
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: '', addressLine1: '', addressLine2: '', city: '', county: '', postcode: '', notes: '', clientId,
  })

  const { data: venues = [], isLoading } = useQuery({
    queryKey: ['venues'],
    queryFn: () => adminApi.getVenues().then(r => r.data),
  })

  const clientVenues = venues.filter(v => v.clientId === clientId)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['venues'] })

  const toPayload = (f: typeof form) => ({
    name: f.name.trim(),
    addressLine1: f.addressLine1.trim() || null,
    addressLine2: f.addressLine2.trim() || null,
    city: f.city.trim() || null,
    county: f.county.trim() || null,
    postcode: f.postcode.trim() || null,
    notes: f.notes.trim() || null,
    clientId,
  })

  const createMutation = useMutation({
    mutationFn: () => adminApi.createVenue(toPayload(form)),
    onSuccess: () => { invalidate(); setAdding(false); resetForm() },
  })

  const updateMutation = useMutation({
    mutationFn: (id: string) => adminApi.updateVenue(id, toPayload(form)),
    onSuccess: () => { invalidate(); setEditId(null); resetForm() },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteVenue(id),
    onSuccess: invalidate,
  })

  function resetForm() { setForm({ name: '', addressLine1: '', addressLine2: '', city: '', county: '', postcode: '', notes: '', clientId }) }
  function cancel() { setAdding(false); setEditId(null); resetForm() }

  function startEdit(v: typeof clientVenues[0]) {
    setEditId(v.id)
    setAdding(false)
    setForm({ name: v.name, addressLine1: v.addressLine1 ?? '', addressLine2: v.addressLine2 ?? '', city: v.city ?? '', county: v.county ?? '', postcode: v.postcode ?? '', notes: v.notes ?? '', clientId })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Venues specific to this client.</p>
        {!adding && !editId && (
          <Button size="sm" onClick={() => { setAdding(true); resetForm() }}>
            <Plus size={15} className="mr-1.5" /> Add venue
          </Button>
        )}
      </div>

      {adding && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin size={15} className="text-indigo-500" /> New venue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VenueFormFields form={form} onChange={f => setForm(f as typeof form)} clients={[]} hideClientSelect />
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={() => createMutation.mutate()} disabled={!form.name.trim() || createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Save venue'}
              </Button>
              <Button size="sm" variant="ghost" onClick={cancel}><X size={14} /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-sm text-gray-400 p-6">Loading…</p>
          ) : clientVenues.length === 0 && !adding ? (
            <div className="text-center py-12">
              <MapPin size={28} className="mx-auto text-gray-200 mb-2" />
              <p className="text-gray-500 text-sm">No venues for this client yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {clientVenues.map(v => (
                editId === v.id ? (
                  <div key={v.id} className="p-4 bg-gray-50">
                    <VenueFormFields form={form} onChange={f => setForm(f as typeof form)} clients={[]} hideClientSelect />
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" onClick={() => updateMutation.mutate(v.id)} disabled={!form.name.trim() || updateMutation.isPending}>
                        {updateMutation.isPending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancel}><X size={14} /></Button>
                    </div>
                  </div>
                ) : (
                  <div key={v.id} className="flex items-start justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm">{v.name}</p>
                      {venueSummary(v) && (
                        <p className="text-sm text-gray-500 mt-0.5 truncate">{venueSummary(v)}</p>
                      )}
                      {v.notes && <p className="text-xs text-gray-400 mt-0.5 truncate">{v.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1 ml-3 shrink-0">
                      <button onClick={() => startEdit(v)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => { if (confirm(`Delete venue "${v.name}"?`)) deleteMutation.mutate(v.id) }}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
